import React, { useState, useEffect, useCallback } from 'react';
import { Product } from '../../types';
import { fetchProducts, fetchProductById } from '../../services/productService';
import Loader from '../loader/Loader';
import Notification from '../toast/Notification';

const PER_PAGE = 20;

interface ProductListForEditProps {
  onSelectProduct: (product: Product) => void;
}

const ProductListForEdit: React.FC<ProductListForEditProps> = ({ onSelectProduct }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingProductId, setLoadingProductId] = useState<number | null>(null);
  const [notification, setNotification] = useState<{ id: number; text: string; status: 'success' | 'error' | 'info' } | null>(null);

  const loadProducts = useCallback(async (p: number, q: string) => {
    try {
      setLoading(true);
      const offset = (p - 1) * PER_PAGE;
      const result = await fetchProducts({
        limit: PER_PAGE,
        offset,
        search: q.trim() || undefined,
      });
      setProducts(result.products || []);
      setTotal(result.total ?? 0);
    } catch (e) {
      console.error(e);
      showNotification('Ошибка загрузки списка товаров', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts(page, search);
  }, [page, search, loadProducts]);

  const showNotification = (text: string, status: 'success' | 'error' | 'info') => {
    setNotification({ id: Date.now(), text, status });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleEdit = async (product: Product) => {
    try {
      setLoadingProductId(product.id);
      const fullProduct = await fetchProductById(product.id);
      onSelectProduct(fullProduct);
    } catch (e) {
      console.error(e);
      showNotification('Ошибка загрузки данных товара', 'error');
    } finally {
      setLoadingProductId(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const hasSearch = search.length > 0;

  return (
    <div className="space-y-4 p-4">
      {notification && (
        <Notification id={notification.id} text={notification.text} status={notification.status} duration={5000} />
      )}

      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Выберите товар для редактирования</h2>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Найдите товар по названию или артикулу и нажмите «Редактировать», чтобы изменить картинки, описание и другие данные.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="product-edit-search">
          Поиск товара
        </label>
        <input
          id="product-edit-search"
          type="text"
          placeholder="Название или артикул..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 min-w-[220px]"
        />
        <button
          type="button"
          onClick={handleSearch}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Найти
        </button>
        {hasSearch && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Сбросить
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <Loader />
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-16">Фото</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Название</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Артикул</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Цена</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-32">Действие</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {products.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      {hasSearch ? 'По запросу ничего не найдено' : 'Нет товаров'}
                    </td>
                  </tr>
                ) : (
                  products.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-2">
                        {product.cover_image ? (
                          <img
                            src={product.cover_image}
                            alt=""
                            className="w-12 h-12 object-contain rounded border border-gray-200 dark:border-gray-600"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded border border-gray-200 dark:border-gray-600 flex items-center justify-center text-xs text-gray-400">—</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100 max-w-[280px] truncate" title={product.name}>
                        {product.name || '—'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 font-mono">{product.sku || '—'}</td>
                      <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{product.price} ₸</td>
                      <td className="px-4 py-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(product)}
                          disabled={loadingProductId === product.id}
                          className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 disabled:opacity-50"
                        >
                          {loadingProductId === product.id ? '...' : 'Редактировать'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Показано {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, total)} из {total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Назад
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Страница {page} из {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Вперёд
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductListForEdit;
